// PHA-573: collapsible "Earlier insights" list shown beneath the active
// insight on the patient chart's AI Insights tab. Read-only — no ack /
// feedback affordances on superseded rows (those live on the active row).

import React, { useState } from 'react';
import { Badge, Box, Card, Flex, Text } from '@radix-ui/themes';
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import type { PatientAiInsight } from '../types';

interface AiInsightHistoryListProps {
  history: PatientAiInsight[];
}

const tierColor = (tier: PatientAiInsight['risk_tier']): 'red' | 'orange' | 'green' => {
  if (tier === 'critical') return 'red';
  if (tier === 'watch') return 'orange';
  return 'green';
};

const formatComputedAt = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export const AiInsightHistoryList: React.FC<AiInsightHistoryListProps> = ({ history }) => {
  const [expanded, setExpanded] = useState(false);
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());

  if (!history || history.length === 0) return null;

  const toggle = (id: number) => {
    const next = new Set(openIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpenIds(next);
  };

  return (
    <Box>
      <Flex
        align="center"
        gap="1"
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((s) => !s)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((s) => !s);
          }
        }}
        style={{ cursor: 'pointer', padding: '8px 0' }}
      >
        {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        <Text size="2" weight="bold">
          Earlier insights ({history.length})
        </Text>
      </Flex>
      {expanded && (
        <Flex direction="column" gap="2">
          {history.map((h) => {
            const isOpen = openIds.has(h.insight_id);
            return (
              <Card key={h.insight_id} variant="surface">
                <Flex
                  align="center"
                  gap="2"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggle(h.insight_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(h.insight_id);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  <Badge color={tierColor(h.risk_tier)} variant="soft" size="1">
                    {h.risk_tier.toUpperCase()}
                  </Badge>
                  <Text size="2" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {h.why_now}
                  </Text>
                  <Text size="1" color="gray">
                    {formatComputedAt(h.computed_at)}
                  </Text>
                  <Badge color="gray" variant="soft" size="1">
                    {h.prompt_version}
                  </Badge>
                </Flex>
                {isOpen && (
                  <Box mt="2">
                    {h.ma_action?.summary && (
                      <Text size="2" as="div" style={{ marginBottom: 4 }}>
                        <strong>Recommended action:</strong> {h.ma_action.summary}
                      </Text>
                    )}
                    {h.md_action?.summary && (
                      <Text size="2" as="div" style={{ marginBottom: 4 }}>
                        <strong>MD consideration:</strong> {h.md_action.summary}
                      </Text>
                    )}
                    {h.trend_callouts?.length > 0 && (
                      <Box mt="1">
                        <Text size="1" color="gray" weight="bold">Trends</Text>
                        <ul style={{ marginTop: 2, paddingLeft: 18 }}>
                          {h.trend_callouts.map((t, idx) => (
                            <li key={idx}>
                              <Text size="2">{t.metric.toUpperCase()}: {t.narrative}</Text>
                            </li>
                          ))}
                        </ul>
                      </Box>
                    )}
                  </Box>
                )}
              </Card>
            );
          })}
        </Flex>
      )}
    </Box>
  );
};

export default AiInsightHistoryList;
